import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import moment, { Moment } from 'moment';
import {
  Button,
  Col,
  Collapse,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Radio,
  Row,
  Select,
  Statistic,
  Table,
  Tag,
  Tooltip
} from 'antd';
import { useHistory } from 'react-router';

import colors from '../../utils/colors';
import { FIRST_WEEK } from '../../utils/constants/labels';
import PageHeader from '../common/layout/PageHeader';
import { handleErrorResponse } from '../../api/network';
import { firstWeekApi } from '../../api/firstWeekApi';
import { actionName } from './actionNames';
import TrendLine, { TrendSeries } from './graphs/TrendLine';
import { notify, NotificationType } from '../../utils/helpers';
import {
  ActionTrend,
  AtRiskRow,
  FirstWeekReport,
  FirstWeekSegment,
  FirstWeekSummary,
  HealthRow,
  HistoryFillStatus,
  MonthRow,
  RankingRow,
  SchoolRow,
  TimingRow,
  WeekRow
} from '../../types/firstWeekTypes';

const { RangePicker } = DatePicker;
const { Panel } = Collapse;

const DATE = 'YYYY-MM-DD';
// The server builds at most this much at once (FirstWeekReportService.MAX_RANGE), to keep the report light.
const MAX_YEARS = 2;
const TIMING_DAYS = ['0', '1', '2', '3', '5', '7'];

// Quick ranges beside the date picker (Petr, 2026-10-21), each ending today (or last month's last day).
const PRESETS: { key: string; label: string; range: () => [Moment, Moment] }[] = [
  { key: '3m', label: '3 months', range: () => [moment().subtract(3, 'months'), moment()] },
  { key: '6m', label: '6 months', range: () => [moment().subtract(6, 'months'), moment()] },
  { key: '12m', label: '12 months', range: () => [moment().subtract(12, 'months'), moment()] },
  { key: '2y', label: '2 years', range: () => [moment().subtract(MAX_YEARS, 'years'), moment()] },
  { key: 'month', label: 'This month', range: () => [moment().startOf('month'), moment()] },
  {
    key: 'lastMonth',
    label: 'Last month',
    range: () => [
      moment()
        .subtract(1, 'month')
        .startOf('month'),
      moment()
        .subtract(1, 'month')
        .endOf('month')
    ]
  }
];

const sameDay = (a: Moment, b: Moment) => a.format(DATE) === b.format(DATE);

// Lines for the graphs, in this order.
const LINE_COLORS = [colors.primaryPurple, '#FBB028', '#FF6473', '#1AB77F', '#3F8EFC', '#B03A48'];

/**
 * A top number against the period before: counts as a % change, rates as points. Green when it moved the good way
 * (`better`: up for trials and paying, down for card failures and cancels).
 */
const Change: FC<{ now: number | null; before: number | null; rate?: boolean; better: 'up' | 'down' | 'neutral' }> = ({
  now,
  before,
  rate,
  better
}) => {
  if (now === null || before === null) return null;
  let text: string;
  let delta: number;
  if (rate) {
    delta = Math.round((now - before) * 100);
    text = `${delta > 0 ? '+' : ''}${delta} pts`;
  } else {
    if (before === 0) return now > 0 ? <ChangeLine style={{ color: changeTone(1, better) }}>new</ChangeLine> : null;
    delta = Math.round(((now - before) / before) * 100);
    text = `${delta > 0 ? '+' : ''}${delta}%`;
  }
  return (
    <ChangeLine style={{ color: changeTone(delta, better) }}>
      {text} <Muted>(was {rate ? `${Math.round(before * 100)}%` : before.toLocaleString()})</Muted>
    </ChangeLine>
  );
};

// Finished trials mostly reflect how recent the dates are (the newest trials haven't ended yet): no colour.
const changeTone = (delta: number, better: 'up' | 'down' | 'neutral') => {
  if (delta === 0 || better === 'neutral') return 'rgba(0, 0, 0, 0.45)';
  const improved = better === 'up' ? delta > 0 : delta < 0;
  return improved ? colors.positive : colors.negative;
};

const share = (count: number | null | undefined, of: number) =>
  count === null || count === undefined || !of ? null : count / of;

const SEGMENTS: { value: FirstWeekSegment; label: string }[] = [
  { value: 'website', label: 'Website trials (paying)' },
  { value: 'school', label: 'School students' },
  { value: 'other', label: 'Free and invited accounts' }
];

const WINDOWS = [
  { value: 1, label: 'First day' },
  { value: 3, label: 'First 3 days' },
  { value: 7, label: 'First week' }
];

const pct = (value: number | null | undefined) =>
  value === null || value === undefined ? '–' : `${Math.round(value * 100)}%`;

const points = (value: number | null | undefined) => {
  if (value === null || value === undefined) return '–';
  const rounded = Math.round(value * 100);
  return `${rounded > 0 ? '+' : ''}${rounded} pts`;
};

// Green for "goes with the good outcome", red for the opposite, stronger the bigger the gap.
const diffColor = (value: number | null | undefined) => {
  if (value === null || value === undefined) return 'transparent';
  const strength = Math.min(Math.abs(value) / 0.3, 1) * 0.35;
  return value >= 0 ? `rgba(46, 160, 67, ${strength})` : `rgba(218, 54, 51, ${strength})`;
};

const FirstWeek: FC = () => {
  const history = useHistory();
  const [segment, setSegment] = useState<FirstWeekSegment>('website');
  const [range, setRange] = useState<[Moment, Moment]>(() => PRESETS[1].range());
  const [windowDays, setWindowDays] = useState(3);
  const [report, setReport] = useState<FirstWeekReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [health, setHealth] = useState<HealthRow[]>([]);
  const [fill, setFill] = useState<HistoryFillStatus | null>(null);
  const [fillFrom, setFillFrom] = useState<Moment>(moment('2024-07-29'));

  const load = useCallback(async () => {
    setLoading(true);
    const response = await firstWeekApi.getReport(segment, range[0].format(DATE), range[1].format(DATE));
    setLoading(false);
    if (response.status === 503) {
      notify('The report is being built for another request. Try again in a minute.', NotificationType.WARNING);
      return;
    }
    if (response.status === 504) {
      // A query ran past the backend's 60-second limit and was stopped (ReportQueries), or the page waited too long.
      notify(
        "The report took too long and was stopped, so it can't slow the app. Try fewer days.",
        NotificationType.WARNING
      );
      return;
    }
    if (!response.ok) {
      handleErrorResponse(response, history);
      return;
    }
    if (response.data) setReport(response.data);
  }, [segment, range, history]);

  const changeRange = (start: Moment, end: Moment) => {
    const earliest = end.clone().subtract(MAX_YEARS, 'years');
    if (start.isBefore(earliest)) {
      notify(`Up to ${MAX_YEARS} years at a time: starting ${earliest.format(DATE)}`, NotificationType.WARNING);
      setRange([earliest, end]);
    } else {
      setRange([start, end]);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const loadHealth = useCallback(async () => {
    const [healthResponse, fillResponse] = await Promise.all([firstWeekApi.getHealth(14), firstWeekApi.getHistoryFill()]);
    if (healthResponse.ok && healthResponse.data) setHealth(healthResponse.data);
    if (fillResponse.ok && fillResponse.data) setFill(fillResponse.data);
  }, []);

  useEffect(() => {
    loadHealth();
  }, [loadHealth]);

  // Refresh takes everything on the page, the panels below included: they only loaded when the page opened, so a
  // check of tracking after clicking around in the app showed nothing new (Petr, 2026-10-20).
  const refresh = useCallback(() => {
    load();
    loadHealth();
  }, [load, loadHealth]);

  const isWebsite = segment === 'website';
  const outcome = isWebsite ? 'Paid' : 'Still using (weeks 4–8)';

  const ranking = useMemo(() => (report ? report.ranking.filter(row => row.days === windowDays) : []), [report, windowDays]);
  const knownEvents = useMemo(() => {
    const events = new Set<string>(['active_days']);
    report?.ranking.forEach(row => {
      if (!row.event.startsWith('active_')) events.add(row.event);
    });
    return Array.from(events).sort();
  }, [report]);

  const rankingColumns = (outcomeWord: string) => [
    {
      title: 'Action',
      dataIndex: 'event',
      render: (event: string, row: RankingRow) => (
        <span style={{ opacity: row.lowSample ? 0.5 : 1 }}>
          {actionName(event)}
          {row.lowSample && (
            <Tooltip title="Fewer than 30 students on one side: treat as a hint, not an answer.">
              <Tag style={{ marginLeft: 8 }}>few</Tag>
            </Tooltip>
          )}
        </span>
      )
    },
    { title: 'Did it', render: (row: RankingRow) => `${row.yes} (${pct(row.yes / (row.yes + row.no))})` },
    {
      title: `${outcomeWord} if yes`,
      render: (row: RankingRow) => (
        <Tooltip title={row.yesRange ? `Likely between ${pct(row.yesRange[0])} and ${pct(row.yesRange[1])}` : ''}>
          <span>{pct(row.yesRate)}</span>
        </Tooltip>
      )
    },
    { title: `${outcomeWord} if no`, render: (row: RankingRow) => pct(row.noRate) },
    {
      title: 'Difference',
      dataIndex: 'diff',
      render: (value: number | null) => <Cell style={{ background: diffColor(value) }}>{points(value)}</Cell>
    },
    {
      title: (
        <Tooltip title="The difference among students who were equally active (1, 2–3, 4+ active days). This is the column to trust: it removes 'keen students do everything'.">
          <span>Among equally active ⓘ</span>
        </Tooltip>
      ),
      dataIndex: 'adjustedDiff',
      render: (value: number | null) => <Cell style={{ background: diffColor(value) }}>{points(value)}</Cell>
    }
  ];

  const timingColumns = [
    { title: 'Action', dataIndex: 'event', render: (event: string) => actionName(event) },
    ...TIMING_DAYS.map(day => ({
      title: day === '0' ? 'By end of day 0' : `By day ${day}`,
      render: (row: TimingRow) => {
        const cell = row.byDay[day];
        if (!cell) return '–';
        const gap = cell.yesRate !== null && cell.noRate !== null ? cell.yesRate - cell.noRate : null;
        return (
          <Tooltip
            title={`${cell.yes} students had done it by then. ${outcome}: ${pct(cell.yesRate)} if yes, ${pct(
              cell.noRate
            )} if not.`}
          >
            <Cell style={{ background: diffColor(gap) }}>{points(gap)}</Cell>
          </Tooltip>
        );
      }
    })),
    {
      title: `Hours to first (${isWebsite ? 'payers' : 'stayers'} / others)`,
      render: (row: TimingRow) =>
        `${row.medianHoursToFirstYes === null ? '–' : Math.round(row.medianHoursToFirstYes)} / ${
          row.medianHoursToFirstNo === null ? '–' : Math.round(row.medianHoursToFirstNo)
        }`
    }
  ];

  const rebuiltTag = (
    <Tooltip title="Rebuilt history: undercounts deleted and unplanned items, and has no Auto-plan, AI app or calendar events.">
      <Tag style={{ marginLeft: 8 }}>rebuilt</Tag>
    </Tooltip>
  );

  // Trial→paid as the funnel sheet lists it, inside Shovel (Petr, 2026-10-20). Rates are of finished trials only, so a
  // month with trials still running shows how they're going, not where they'll end.
  const monthColumns = [
    {
      title: 'Month started',
      dataIndex: 'month',
      render: (month: string, row: MonthRow) => (
        <span>
          {moment(month, 'YYYY-MM').format('MMM YYYY')}
          {row.rebuiltHistory && rebuiltTag}
        </span>
      )
    },
    { title: isWebsite ? 'Trials' : 'Signed up', dataIndex: 'members' },
    ...(isWebsite
      ? [
          { title: 'Paid', dataIndex: 'paid' },
          { title: 'Trial→paid', dataIndex: 'outcomeRate', render: pct },
          { title: 'Cancelled in trial', dataIndex: 'cancelledInTrial' },
          { title: 'Card failed', dataIndex: 'cardFailed' }
        ]
      : [
          { title: 'Still using', dataIndex: 'outcomeYes' },
          { title: outcome, dataIndex: 'outcomeRate', render: pct }
        ]),
    {
      title: isWebsite ? 'Still in trial' : 'Not known yet',
      render: (row: MonthRow) => row.members - row.decided || ''
    }
  ];

  // The weekly groups as lines, oldest first: the outcome (and, for website trials, 24-hour cancels). Weeks with nobody
  // decided yet leave a gap; change notes are the dashed lines.
  const weekSeries: TrendSeries[] = useMemo(() => {
    const weeks = [...(report?.weeks || [])].sort((a, b) => a.weekStart.localeCompare(b.weekStart));
    const line = (id: string, color: string, rate: (week: WeekRow) => number | null) => ({
      id,
      color,
      points: weeks.map(week => {
        const value = rate(week);
        return {
          x: week.weekStart,
          y: value === null || value === undefined ? null : value * 100,
          note: `Week of ${moment(week.weekStart).format('MMM D')}: ${week.members} started, ${week.decided} decided`
        };
      })
    });
    return [
      line(outcome, colors.primaryPurple, week => week.outcomeRate),
      ...(isWebsite ? [line('Cancelled within 24 h', colors.negative, week => week.cancelWithin1dRate)] : [])
    ];
  }, [report, outcome, isWebsite]);

  const weekColumns = [
    {
      title: 'Week starting',
      dataIndex: 'weekStart',
      render: (week: string, row: WeekRow) => (
        <span>
          {week}
          {row.rebuiltHistory && rebuiltTag}
        </span>
      )
    },
    { title: 'Started', dataIndex: 'members' },
    { title: outcome, dataIndex: 'outcomeRate', render: pct },
    ...(isWebsite
      ? [
          { title: 'Card failed', dataIndex: 'cardFailedRate', render: pct },
          { title: 'Cancelled in trial', dataIndex: 'cancelledInTrialRate', render: pct },
          { title: 'Cancelled within 24 h', dataIndex: 'cancelWithin1dRate', render: pct }
        ]
      : []),
    ...(report?.milestones || []).map(milestone => ({
      title: milestone.label,
      render: (row: WeekRow) => pct(row.milestoneRates[String(milestone.id)])
    })),
    {
      title: 'Changes',
      render: (row: WeekRow) => {
        const start = moment(row.weekStart);
        const end = start.clone().add(7, 'days');
        return (report?.notes || [])
          .filter(note => !moment(note.date).isBefore(start) && moment(note.date).isBefore(end))
          .map(note => <Tag key={note.id}>{note.text}</Tag>);
      }
    }
  ];

  const addMilestone = async (values: any) => {
    const response = await firstWeekApi.createMilestone({
      segment,
      event: values.event,
      minCount: values.minCount,
      byDay: values.byDay,
      label: values.label
    });
    if (response.ok) load();
    else handleErrorResponse(response, history);
  };

  const removeMilestone = async (id: number) => {
    const response = await firstWeekApi.deleteMilestone(id);
    if (response.ok) load();
    else handleErrorResponse(response, history);
  };

  const addNote = async (values: any) => {
    const response = await firstWeekApi.createNote(values.date.format(DATE), values.text);
    if (response.ok) load();
    else handleErrorResponse(response, history);
  };

  const removeNote = async (id: number) => {
    const response = await firstWeekApi.deleteNote(id);
    if (response.ok) load();
    else handleErrorResponse(response, history);
  };

  const startFill = async () => {
    const response = await firstWeekApi.startHistoryFill(fillFrom.format(DATE));
    if (response.data) setFill(response.data);
    if (!response.ok && response.status !== 409) handleErrorResponse(response, history);
  };

  const summary = report?.summary;
  const before: FirstWeekSummary | null = report?.previousSummary || null;
  const notes = (report?.notes || []).map(note => ({ date: note.date, label: note.text }));

  // Month by month, oldest first: how each month's trials (or first weeks) ended, as shares of those finished.
  const monthSeries: TrendSeries[] = useMemo(() => {
    const months = [...(report?.months || [])].sort((a, b) => a.month.localeCompare(b.month));
    const line = (id: string, color: string, value: (month: MonthRow) => number | null) => ({
      id,
      color,
      points: months.map(month => {
        const rate = value(month);
        return {
          x: `${month.month}-01`,
          y: rate === null ? null : rate * 100,
          note: `${month.members.toLocaleString()} started, ${month.decided.toLocaleString()} finished`
        };
      })
    });
    return [
      line(outcome, LINE_COLORS[0], month => (month.decided ? month.outcomeRate : null)),
      ...(isWebsite
        ? [
            line('Card failed', LINE_COLORS[1], month => share(month.cardFailed, month.decided)),
            line('Cancelled in trial', LINE_COLORS[2], month => share(month.cancelledInTrial, month.decided)),
            line('Cancelled within 24 h', LINE_COLORS[5], month => share(month.cancelWithin1d, month.members))
          ]
        : [])
    ];
  }, [report, outcome, isWebsite]);

  // Week by week, oldest first: how many started, and the share of each week's group that hit each target.
  const weeksAscending = useMemo(() => [...(report?.weeks || [])].sort((a, b) => a.weekStart.localeCompare(b.weekStart)), [
    report
  ]);
  const startsSeries: TrendSeries[] = [
    {
      id: isWebsite ? 'Trials started' : 'Students started',
      color: LINE_COLORS[0],
      points: weeksAscending.map(week => ({
        x: week.weekStart,
        y: week.members,
        note: `Week of ${moment(week.weekStart).format('MMM D')}`
      }))
    }
  ];
  const targetSeries: TrendSeries[] = (report?.milestones || []).map((milestone, index) => ({
    id: milestone.label,
    color: LINE_COLORS[index % LINE_COLORS.length],
    points: weeksAscending.map(week => {
      const rate = week.milestoneRates[String(milestone.id)];
      return {
        x: week.weekStart,
        y: rate === null || rate === undefined ? null : rate * 100,
        note: `Week of ${moment(week.weekStart).format('MMM D')}: ${week.members} started`
      };
    })
  }));

  // The top 3 actions of the chosen window, month by month: are more students doing what goes with the outcome?
  const actionSeries: TrendSeries[] = (report?.actionTrends || [])
    .filter((trend: ActionTrend) => trend.days === windowDays)
    .map((trend: ActionTrend, index: number) => ({
      id: actionName(trend.event),
      color: LINE_COLORS[index % LINE_COLORS.length],
      points: [...trend.months]
        .sort((a, b) => a.month.localeCompare(b.month))
        .map(month => ({
          x: `${month.month}-01`,
          y: month.rate === null ? null : month.rate * 100,
          note: `${month.members.toLocaleString()} started`
        }))
    }));
  const days = (from: string, to: string) => moment(to).diff(moment(from), 'days') + 1;

  return (
    <Container>
      <PageHeader title={FIRST_WEEK} />
      <Controls>
        <Select value={segment} onChange={(value: FirstWeekSegment) => setSegment(value)} style={{ width: 240 }}>
          {SEGMENTS.map(option => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
        <Radio.Group
          value={
            PRESETS.find(preset => {
              const [start, end] = preset.range();
              return sameDay(start, range[0]) && sameDay(end, range[1]);
            })?.key
          }
          onChange={event => {
            const preset = PRESETS.find(option => option.key === event.target.value);
            if (preset) setRange(preset.range());
          }}
        >
          {PRESETS.map(preset => (
            <Radio.Button key={preset.key} value={preset.key}>
              {preset.label}
            </Radio.Button>
          ))}
        </Radio.Group>
        <RangePicker
          value={range}
          allowClear={false}
          onChange={values => values && values[0] && values[1] && changeRange(values[0], values[1])}
        />
        <Button onClick={refresh} loading={loading}>
          Refresh
        </Button>
        {report && <Muted>Updated {moment(report.generatedAt).fromNow()}</Muted>}
      </Controls>

      {summary && (
        <Section>
          <Row gutter={16}>
            <Col span={4}>
              <Statistic title={isWebsite ? 'Trials started' : 'Students started'} value={summary.members} />
              <Change now={summary.members} before={before && before.members} better="up" />
            </Col>
            <Col span={4}>
              <Statistic title={isWebsite ? 'Trials finished' : 'Past week 8'} value={summary.decided} />
              <Change now={summary.decided} before={before && before.decided} better="neutral" />
            </Col>
            <Col span={4}>
              <Statistic title={outcome} value={pct(summary.outcomeRate)} />
              <Change now={summary.outcomeRate} before={before && before.outcomeRate} rate better="up" />
            </Col>
            {isWebsite && (
              <>
                <Col span={4}>
                  <Statistic
                    title="Card failed"
                    value={summary.cardFailed ?? 0}
                    suffix={<Muted>{pct(share(summary.cardFailed, summary.decided))}</Muted>}
                  />
                  <Change
                    now={share(summary.cardFailed, summary.decided)}
                    before={before && share(before.cardFailed, before.decided)}
                    rate
                    better="down"
                  />
                </Col>
                <Col span={4}>
                  <Statistic
                    title="Cancelled in trial"
                    value={summary.cancelledInTrial ?? 0}
                    suffix={<Muted>{pct(share(summary.cancelledInTrial, summary.decided))}</Muted>}
                  />
                  <Change
                    now={share(summary.cancelledInTrial, summary.decided)}
                    before={before && share(before.cancelledInTrial, before.decided)}
                    rate
                    better="down"
                  />
                </Col>
                <Col span={4}>
                  <Statistic
                    title="Cancelled in 24 h"
                    value={summary.cancelWithin1d ?? 0}
                    suffix={<Muted>{pct(share(summary.cancelWithin1d, summary.members))}</Muted>}
                  />
                  <Change
                    now={share(summary.cancelWithin1d, summary.members)}
                    before={before && share(before.cancelWithin1d, before.members)}
                    rate
                    better="down"
                  />
                </Col>
              </>
            )}
          </Row>
          {report?.previousFrom && report.previousTo && (
            <Hint style={{ marginTop: 8, marginBottom: 0 }}>
              {before
                ? `Compared with the ${days(report.previousFrom, report.previousTo)} days before: ${moment(
                    report.previousFrom
                  ).format('MMM D, YYYY')} – ${moment(report.previousTo).format(
                    'MMM D, YYYY'
                  )}. Rates are of finished trials (24-hour cancels: of all started).`
                : "No comparison: the period before starts before the log's history does, so it would look idle."}
            </Hint>
          )}
        </Section>
      )}

      <Section>
        <SectionTitle>
          What goes with {isWebsite ? 'paying' : 'still using Shovel in weeks 4–8'}
          <Select
            value={windowDays}
            onChange={(value: number) => setWindowDays(value)}
            style={{ width: 160, marginLeft: 16 }}
          >
            {WINDOWS.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </SectionTitle>
        <Hint>
          Every action students took, ranked. "Difference" compares those who did it with those who didn't; "Among equally
          active" compares students with the same number of active days, so it isn't fooled by keen students doing
          everything. Shows what goes together, not what causes what: test a nudge to be sure.
        </Hint>
        <Table
          size="small"
          loading={loading}
          rowKey={(row: RankingRow) => `${row.event}-${row.days}`}
          columns={rankingColumns(isWebsite ? 'Paid' : 'Still using')}
          dataSource={ranking}
          pagination={{ pageSize: 25 }}
        />
        {actionSeries.length > 0 && actionSeries[0].points.length > 1 && (
          <GraphBox>
            <SubTitle>Top 3 actions, month by month</SubTitle>
            <Muted>
              Of each month&apos;s starters, the share who did them in the{' '}
              {WINDOWS.find(w => w.value === windowDays)?.label.toLowerCase()}. Rising lines: more students now do what goes
              with {isWebsite ? 'paying' : 'staying'}.
            </Muted>
            <TrendLine percent series={actionSeries} markers={notes} dateFormat="MMM YYYY" />
          </GraphBox>
        )}
      </Section>

      <Section>
        <SectionTitle>By when it matters</SectionTitle>
        <Hint>
          For each action: how much more often students {isWebsite ? 'paid' : 'stayed'} if they had done it by that day. When
          the gap shrinks to nothing, doing it later no longer helps; that's the deadline.
        </Hint>
        <Table
          size="small"
          loading={loading}
          rowKey={(row: TimingRow) => row.event}
          columns={timingColumns}
          dataSource={report?.timing || []}
          pagination={{ pageSize: 15 }}
        />
      </Section>

      {isWebsite && report?.staying && (
        <Section>
          <SectionTitle>What goes with staying (first 4 paid weeks)</SectionTitle>
          <Hint>
            Monthly: still paying 90 days after the first payment ({report.staying.monthlyMembers} students,{' '}
            {pct(report.staying.monthlyRate)} stayed). Yearly: auto-renew still on at day 90 ({report.staying.annualMembers}{' '}
            students, {pct(report.staying.annualRate)}).
          </Hint>
          <div>
            <div>
              <SubTitle>Monthly</SubTitle>
              <Table
                size="small"
                rowKey={(row: RankingRow) => row.event}
                columns={rankingColumns('Stayed').filter((_, index) => index !== 5)}
                dataSource={report.staying.monthly}
                pagination={{ pageSize: 15 }}
              />
            </div>
            <div>
              <SubTitle>Yearly</SubTitle>
              <Table
                size="small"
                rowKey={(row: RankingRow) => row.event}
                columns={rankingColumns('Renewal on').filter((_, index) => index !== 5)}
                dataSource={report.staying.annual}
                pagination={{ pageSize: 15 }}
              />
            </div>
          </div>
        </Section>
      )}

      <Section>
        <SectionTitle>Month by month</SectionTitle>
        <Hint>
          {isWebsite
            ? "Every trial by the month it started, and how it ended. Trial→paid is of finished trials, so a month with trials still running isn't final."
            : 'Every school student by the month they started, and whether they were still using Shovel in weeks 4–8.'}{' '}
          Follows the dates above (up to {MAX_YEARS} years): widen them to see older months.
        </Hint>
        {monthSeries[0].points.length > 1 && (
          <GraphBox>
            <TrendLine percent series={monthSeries} markers={notes} dateFormat="MMM YYYY" />
          </GraphBox>
        )}
        <Table
          size="small"
          loading={loading}
          rowKey={(row: MonthRow) => row.month}
          columns={monthColumns}
          dataSource={report?.months || []}
          pagination={{ pageSize: 24 }}
        />
      </Section>

      <Section>
        <SectionTitle>Week by week</SectionTitle>
        <Hint>The targets' columns are the numbers to move. Notes mark when something changed, for before and after.</Hint>
        {weekSeries[0].points.length > 1 && (
          <GraphBox>
            <TrendLine percent series={weekSeries} markers={notes} />
          </GraphBox>
        )}
        {targetSeries.length > 0 && weeksAscending.length > 1 && (
          <GraphBox>
            <SubTitle>Targets reached, by week</SubTitle>
            <Muted>Of each week&apos;s group, the share who hit each target (once its deadline has passed).</Muted>
            <TrendLine percent legend series={targetSeries} markers={notes} />
          </GraphBox>
        )}
        {weeksAscending.length > 1 && (
          <GraphBox>
            <SubTitle>{isWebsite ? 'Trials started' : 'Students started'}, by week</SubTitle>
            <TrendLine series={startsSeries} markers={notes} height={160} />
          </GraphBox>
        )}
        <Table
          size="small"
          loading={loading}
          rowKey={(row: WeekRow) => row.weekStart}
          columns={weekColumns}
          dataSource={report?.weeks || []}
          pagination={{ pageSize: 20 }}
        />
      </Section>

      <Section>
        <SectionTitle>Targets</SectionTitle>
        <Hint>Pick from the rankings, e.g. "Planned a study session, at least 3, by day 3".</Hint>
        {(report?.milestones || []).map(milestone => (
          <ListLine key={milestone.id}>
            <span>
              <strong>{milestone.label}</strong>
              <br />
              <Muted>
                {actionName(milestone.event)}: at least {milestone.minCount}, by day {milestone.byDay}
              </Muted>
            </span>
            {milestone.id > 0 ? (
              <Popconfirm title="Remove this target?" onConfirm={() => removeMilestone(milestone.id)}>
                <Button size="small" type="link">
                  Remove
                </Button>
              </Popconfirm>
            ) : (
              <Muted>default until you add your own</Muted>
            )}
          </ListLine>
        ))}
        <Form layout="inline" onFinish={addMilestone} style={{ marginTop: 12 }}>
          <Form.Item label="Action" name="event" rules={[{ required: true, message: 'Pick an action' }]}>
            <Select showSearch optionFilterProp="children" placeholder="Action" style={{ width: 220 }}>
              {knownEvents.map(event => (
                <Select.Option key={event} value={event}>
                  {actionName(event)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="At least" name="minCount" initialValue={1} rules={[{ required: true }]}>
            <InputNumber min={1} max={1000} placeholder="At least" />
          </Form.Item>
          <Form.Item label="By day" name="byDay" initialValue={3} rules={[{ required: true }]}>
            <InputNumber min={0} max={7} placeholder="By day" />
          </Form.Item>
          <Form.Item label="Name" name="label" rules={[{ required: true, message: 'Name it' }]}>
            <Input placeholder="Name, e.g. Planned 3 sessions by day 3" style={{ width: 260 }} />
          </Form.Item>
          <Button htmlType="submit">Add target</Button>
        </Form>
      </Section>
      <Section>
        <SectionTitle>Change notes</SectionTitle>
        <Hint>Write down what you changed and when, so the week-by-week table shows before and after.</Hint>
        {(report?.notes || []).map(note => (
          <ListLine key={note.id}>
            <span>
              <Muted>{note.date}</Muted> {note.text}
            </span>
            <Popconfirm title="Remove this note?" onConfirm={() => removeNote(note.id)}>
              <Button size="small" type="link">
                Remove
              </Button>
            </Popconfirm>
          </ListLine>
        ))}
        <Form layout="inline" onFinish={addNote} style={{ marginTop: 12 }}>
          <Form.Item label="Date" name="date" initialValue={moment()} rules={[{ required: true }]}>
            <DatePicker />
          </Form.Item>
          <Form.Item label="What changed" name="text" rules={[{ required: true, message: 'What changed?' }]}>
            <Input placeholder="e.g. Day-2 reminder email live" style={{ width: 280 }} />
          </Form.Item>
          <Button htmlType="submit">Add note</Button>
        </Form>
      </Section>

      <Section>
        <SectionTitle>Behind on a target</SectionTitle>
        <Hint>Students still in their trial (or first weeks) who missed a target's deadline. For a personal nudge.</Hint>
        <Table
          size="small"
          rowKey={(row: AtRiskRow) => `${row.userId}-${row.milestoneId}`}
          columns={[
            { title: 'Student id', dataIndex: 'userId' },
            { title: 'Started', dataIndex: 'startedAt', render: (value: string) => moment(value).format('MMM D, HH:mm') },
            { title: 'Missed', dataIndex: 'label' },
            { title: 'Deadline was', dataIndex: 'deadline', render: (value: string) => moment(value).fromNow() }
          ]}
          dataSource={report?.atRisk || []}
          pagination={{ pageSize: 10 }}
        />
      </Section>

      {segment === 'school' && (
        <Section>
          <SectionTitle>Schools</SectionTitle>
          <Table
            size="small"
            rowKey={(row: SchoolRow) => String(row.organizationId)}
            columns={[
              { title: 'School', dataIndex: 'name' },
              { title: 'Sign-ups', dataIndex: 'signups' },
              { title: 'Active in week 1', dataIndex: 'activeWeek1Rate', render: pct },
              { title: 'Past week 8', dataIndex: 'matured' },
              { title: 'Still using in weeks 4–8', dataIndex: 'activeWeeks4to8Rate', render: pct }
            ]}
            dataSource={report?.schools || []}
            pagination={{ pageSize: 20 }}
          />
        </Section>
      )}

      <Collapse style={{ margin: '0 24px 24px' }} onChange={loadHealth}>
        <Panel header="Tracking health (last 14 days)" key="health">
          <Hint>Rows per action per day. A gap or a sudden drop means tracking broke somewhere.</Hint>
          <Table
            size="small"
            rowKey={(row: HealthRow) => `${row.day}-${row.event}-${row.source}`}
            columns={[
              { title: 'Day', dataIndex: 'day' },
              { title: 'Action', dataIndex: 'event', render: (event: string) => actionName(event) },
              { title: 'From', dataIndex: 'source' },
              { title: 'Rows', dataIndex: 'rows' }
            ]}
            dataSource={health}
            pagination={{ pageSize: 30 }}
          />
        </Panel>
        <Panel header="History fill (one time)" key="fill">
          <Hint>
            Rebuilds past sign-ups, tasks, completions, planned sessions and each subscription's Stripe history (read only),
            so the page has answers on day one. Safe to run again: nothing is added twice.
          </Hint>
          <Controls>
            <DatePicker value={fillFrom} allowClear={false} onChange={value => value && setFillFrom(value)} />
            <Popconfirm title="Start the history fill? It runs in the background for several minutes." onConfirm={startFill}>
              <Button disabled={fill?.running}>Fill history from this date</Button>
            </Popconfirm>
            <Button onClick={loadHealth}>Check status</Button>
            {fill && (
              <Muted>
                {fill.running ? 'Running' : 'Last run'}: {fill.step}, {fill.rows} rows
                {fill.error ? ` — error: ${fill.error}` : ''}
              </Muted>
            )}
          </Controls>
        </Panel>
      </Collapse>
    </Container>
  );
};

export default FirstWeek;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  background-color: ${colors.white};
  position: relative;
  padding-bottom: 24px;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 24px 16px;
  flex-wrap: wrap;
`;

const Section = styled.div`
  padding: 8px 24px 24px;
`;

const SectionTitle = styled.h3`
  display: flex;
  align-items: center;
  margin-bottom: 4px;
`;

const GraphBox = styled.div`
  max-width: 900px;
  margin: 8px 0 16px;
`;

const ChangeLine = styled.div`
  font-size: 13px;
  margin-top: 2px;
`;

const SubTitle = styled.h4`
  margin: 8px 0;
`;

const Hint = styled.p`
  color: rgba(0, 0, 0, 0.55);
  max-width: 900px;
  margin-bottom: 12px;
`;

const Muted = styled.span`
  color: rgba(0, 0, 0, 0.45);
`;

const Cell = styled.span`
  display: inline-block;
  min-width: 64px;
  padding: 2px 6px;
  border-radius: 4px;
`;

const ListLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  max-width: 900px;
  padding: 6px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
`;
